'use client';

import { MapPinIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { ArrowTopRightOnSquareIcon, GlobeAltIcon, MapIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { trackGoogleEvent } from '../../../lib/analytics/google';

const WHATSAPP_ICON_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d={WHATSAPP_ICON_PATH} />
  </svg>
);

/**
 * Lo esencial de la ficha arriba de todo: acciones (WhatsApp, llamar, cómo llegar, sitio) y las filas
 * de dirección, teléfono y sitio, donde toda la fila es el link (antes solo el texto respondía).
 * Las URL llegan armadas del servidor (lib/directory-place.js, utils/phone.js).
 */
export default function PlaceContact({
  placeId,
  title,
  address,
  locality,
  phone,
  callHref,
  whatsappHref,
  directionsHref,
  addressHref,
  website
}) {
  const track = (linkType, lead) => {
    trackEvent(EVENTS.DIRECTORY_LINK_CLICKED, { link_type: linkType, link_name: title, business_id: placeId });
    if (lead) {
      trackGoogleEvent('generate_lead', { lead_source: `directory_${linkType}`, business_id: placeId, business_name: title });
    }
  };

  const actions = [
    whatsappHref && {
      key: 'whatsapp',
      href: whatsappHref,
      label: 'WhatsApp',
      icon: <WhatsAppIcon className="h-[18px] w-[18px]" />,
      external: true,
      lead: true,
      tone: 'bg-emerald-500 text-white hover:bg-emerald-600 border-transparent'
    },
    callHref && {
      key: 'call',
      href: callHref,
      label: 'Llamar',
      icon: <PhoneIcon className="h-4 w-4" />,
      lead: true
    },
    directionsHref && {
      key: 'directions',
      href: directionsHref,
      label: 'Cómo llegar',
      icon: <MapIcon className="h-[18px] w-[18px]" />,
      external: true
    },
    website && {
      key: 'website',
      href: website.href,
      label: website.label,
      icon: <GlobeAltIcon className="h-[18px] w-[18px]" />,
      external: true
    }
  ].filter(Boolean);
  // La primera acción va llena (WhatsApp si hay; si no, la primera que haya).
  if (actions[0] && !actions[0].tone) {
    actions[0] = { ...actions[0], tone: 'bg-primary-500 text-white hover:bg-primary-600 border-transparent' };
  }

  const rowClass =
    'flex items-start gap-3 p-4 sm:p-5 transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none first:rounded-t-2xl last:rounded-b-2xl';

  return (
    <>
      {actions.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap [&>*:last-child:nth-child(odd)]:col-span-2">
          {actions.map((action) => (
            <a
              key={action.key}
              href={action.href}
              {...(action.external && { target: '_blank', rel: 'noopener noreferrer' })}
              onClick={() => track(action.key, action.lead)}
              className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-[14px] font-medium transition-colors active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ${action.tone || 'border-gray-200 bg-white text-[#1a1a1a] hover:bg-gray-50'}`}
            >
              {action.icon}
              {action.label}
            </a>
          ))}
        </div>
      )}

      {(address || callHref || website) && (
        <div className="mt-6 rounded-2xl bg-white border border-gray-100 divide-y divide-gray-100">
          {address && (
            <a
              href={addressHref || directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('address')}
              className={rowClass}
            >
              <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium text-[#1a1a1a]">{address}</span>
                {locality && <span className="block text-[12px] text-gray-500 mt-0.5">{locality}</span>}
              </span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" aria-hidden="true" />
            </a>
          )}
          {callHref && (
            <a href={callHref} onClick={() => track('phone', true)} className={rowClass}>
              <PhoneIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 text-[14px] font-medium text-primary-600 tabular-nums">{phone}</span>
            </a>
          )}
          {website && (
            <a
              href={website.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('website_row')}
              className={rowClass}
            >
              <GlobeAltIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-primary-600">{website.display}</span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" aria-hidden="true" />
            </a>
          )}
        </div>
      )}
    </>
  );
}
